import type { ActivityMetadata, NotificationAction, NotificationCategory } from "./types";

const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;
const MS_PER_HOUR = 3_600_000;
const MAX_BADGE_COUNT = 9;

/** Format an unread count for a badge, capping at "9+" so badges stay compact. */
export function formatBadgeCount(count: number): string {
  return count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : `${count}`;
}

/** Coarse recency buckets used to section notification lists. */
export type TimeGroup = "Today" | "This week" | "Earlier";

/** Buckets a timestamp into a coarse recency group for sectioned lists. */
export function timeGroup(iso: string): TimeGroup {
  const diffHours = (Date.now() - new Date(iso).getTime()) / MS_PER_HOUR;
  if (diffHours < HOURS_PER_DAY) {
    return "Today";
  }
  if (diffHours < HOURS_PER_DAY * DAYS_PER_WEEK) {
    return "This week";
  }
  return "Earlier";
}

/** Display order for notification recency sections. */
export const TIME_GROUP_ORDER: TimeGroup[] = ["Today", "This week", "Earlier"];

/** Human-readable labels for the ERP-aligned notification categories. */
export const categoryLabels: Record<NotificationCategory, string> = {
  invoice: "Invoices",
  estimate: "Estimates",
  payment: "Payments",
  credit: "Credits",
  expense: "Expenses",
  purchase_order: "Purchase orders",
  security: "Security",
  system: "System",
};

const METADATA_KEY_LABELS: Record<string, string> = {
  amount: "amount",
  total_amount: "total",
  invoice_id: "invoice",
  account_id: "account",
  estimate_id: "estimate",
  purchase_order_id: "purchase order",
  credit_id: "credit",
  payment_status: "payment status",
  balance: "balance",
};

function labelForMetadataKey(key: string): string {
  return METADATA_KEY_LABELS[key] ?? key.replace(/_/g, " ");
}

/**
 * Builds a short secondary line summarizing an activity row's metadata for
 * the bell/row body text. Deliberately field-name-only (no dollar-amount
 * formatting): `metadata` values are raw DB values whose units vary by
 * column, and this change doesn't need to render money — just point at what
 * changed.
 */
export function summarizeActivity(action: NotificationAction, metadata: ActivityMetadata): string {
  if (action === "deleted") {
    return "Removed from the ledger.";
  }

  const keys = metadata ? Object.keys(metadata) : [];
  if (keys.length === 0) {
    return action === "created" ? "Added to the ledger." : "No field changes.";
  }

  const labels = keys.map(labelForMetadataKey).join(", ");
  return action === "created" ? `Recorded ${labels}.` : `Changed ${labels}.`;
}
